import { queryOptions } from '@tanstack/react-query';

import { toConversationError } from './errors';

import { ONE_MINUTE } from '@/constants/time';
import type { components } from '@primum/api-types';
import { fetchClient } from '@/util/fetchClient';

export type MemberInquiry = components['schemas']['UserInquiry'];

/** How many of a member's inquiries staff can choose from when attaching one. */
export const RECENT_INQUIRY_LIMIT = 10;

export const memberInquiryKeys = {
  recent: (userId: number) => ['memberRecentInquiries', userId, RECENT_INQUIRY_LIMIT] as const,
};

async function getMemberRecentInquiries(userId: number): Promise<MemberInquiry[]> {
  try {
    const { data } = await fetchClient.GET('/v2/users/{userId}/inquiries', {
      params: { path: { userId }, query: { limit: RECENT_INQUIRY_LIMIT } },
    });

    return data?.data ?? [];
  } catch (error) {
    throw toConversationError(error);
  }
}

/**
 * A member's most recent inquiries, newest first, for staff to attach one to a
 * support message. The server already leaves out what the member's own list
 * would not show, so every entry is one the member can open; its order is kept
 * as received.
 */
export function getMemberRecentInquiriesQueryOptions(userId: number) {
  return queryOptions({
    queryKey: memberInquiryKeys.recent(userId),
    queryFn: () => getMemberRecentInquiries(userId),
    staleTime: ONE_MINUTE,
  });
}
